import { ArrowRight, BadgeX, ClipboardList, FileCheck2, Inbox, MessageCircle, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { proToPatientMessage, waLink } from "@/lib/whatsapp";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ProDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState(null);
  const isAdmin = user.role === "admin";

  useEffect(() => {
    const url = isAdmin ? "/requests?scope=professional" : "/requests";
    api.get(url).then((r) => setRequests(r.data)).catch(() => setRequests([]));
  }, [isAdmin]);

  const porVerificar = requests?.filter((r) => r.status === "submitted").length || 0;
  const enRevision = requests?.filter((r) => r.status === "in_review").length || 0;
  const pagoRechazado = requests?.filter((r) => r.status === "payment_rejected").length || 0;
  const completadas = requests?.filter((r) => r.status === "completed").length || 0;

  const stats = [
    { label: "Pago por verificar", value: porVerificar, icon: Inbox, bg: "bg-amber-100", fg: "text-amber-700" },
    { label: "En revisión", value: enRevision, icon: ClipboardList, bg: "bg-sky-100", fg: "text-sky-700" },
    { label: "Pago rechazado", value: pagoRechazado, icon: BadgeX, bg: "bg-red-100", fg: "text-red-700" },
    { label: "Completadas", value: completadas, icon: FileCheck2, bg: "bg-emerald-100", fg: "text-emerald-700" },
  ];

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="pro-dashboard">
      <p className="text-sm text-muted-foreground uppercase tracking-widest mb-1">Panel profesional</p>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold">
          {(user.professional_profile?.titulo || "")} {user.name}
        </h1>
        {isAdmin && (
          <Link to="/admin" data-testid="goto-admin-panel-btn">
            <Button variant="outline" className="rounded-full">
              <ShieldCheck className="h-4 w-4 mr-2" strokeWidth={1.5} /> Panel de administración
            </Button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10">
        {stats.map((s) => (
          <div key={s.label} className="border rounded-2xl bg-white p-6 flex items-center gap-4">
            <span className={`h-11 w-11 rounded-xl ${s.bg} flex items-center justify-center`}>
              <s.icon className={`h-5 w-5 ${s.fg}`} strokeWidth={1.5} />
            </span>
            <div>
              <p className="text-2xl font-heading font-bold" data-testid={`pro-stat-${s.label}`}>{s.value}</p>
              <p className="text-sm text-muted-foreground">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-xl sm:text-2xl font-bold mb-6">Solicitudes de pacientes</h2>

      {requests === null && <p className="text-muted-foreground">Cargando...</p>}
      {requests?.length === 0 && (
        <div className="border rounded-2xl bg-white p-12 text-center" data-testid="pro-empty-state">
          <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-muted-foreground">
            Aún no tienes solicitudes. Te notificaremos por correo cuando llegue una nueva.
          </p>
        </div>
      )}

      <div className="grid gap-4">
        {requests?.map((r) => {
          const wa = waLink(
            r.patient_whatsapp,
            proToPatientMessage({
              patientName: `${r.personal?.nombres || ""} ${r.personal?.apellidos || ""}`.trim(),
              folio: r.folio || "",
              proName: user?.name || "",
            }),
          );
          return (
            <div
              key={r.id}
              data-testid={`pro-request-card-${r.id}`}
              className="border rounded-2xl bg-white p-6 flex flex-wrap items-center justify-between gap-4 card-hover"
            >
              <Link to={`/pro/solicitud/${r.id}`} className="flex-1 min-w-[260px]" data-testid={`pro-request-link-${r.id}`}>
                <div className="flex items-center gap-3 mb-1">
                  <p className="font-heading font-semibold">
                    {r.personal?.nombres} {r.personal?.apellidos}
                    {r.folio ? ` · Folio ${r.folio}` : ""}
                  </p>
                  <StatusBadge status={r.status} />
                </div>
                <p className="text-sm text-muted-foreground">
                  {r.personal?.edad} años · {r.files?.length || 0} archivo(s) · Enviada el {fmtDate(r.submitted_at || r.created_at)}
                </p>
              </Link>
              <div className="flex items-center gap-3">
                {wa && (
                  <a href={wa} target="_blank" rel="noreferrer" data-testid={`pro-card-whatsapp-${r.id}`} onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white">
                      <MessageCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> WhatsApp
                    </Button>
                  </a>
                )}
                <Link to={`/pro/solicitud/${r.id}`} className="text-sm font-semibold text-primary flex items-center gap-1">
                  Revisar <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );
}
